import { useCallback, useRef } from "react";
import type { Subscription } from "rxjs";
import { useAppDispatch, useAppSelector, useAppStore } from "@/store/app/hooks";
import { selectCatalogPersistHydration } from "@/features/sparxsEditor/store/selectors/bundleSelectors";
import { subscribeEditorCatalogList } from "@/features/sparxsEditor/lib/catalog/catalogListSubscription";

export interface UseSparxsListOptions {
  onError?: () => void;
}

export type UseSparxsListResult = {
  buildList: () => void;
  cancelBuildList: () => void;
  catalogPersistReady: boolean;
};

export const useListLoad = (
  options?: UseSparxsListOptions,
): UseSparxsListResult => {
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const {
    searchIndexPersistRehydrated,
    sparxsListPersistRehydrated,
    blueprintCachePersistRehydrated,
  } = useAppSelector(selectCatalogPersistHydration);

  const catalogPersistReady =
    searchIndexPersistRehydrated === true &&
    sparxsListPersistRehydrated === true &&
    blueprintCachePersistRehydrated === true;

  const subscriptionRef = useRef<Subscription | null>(null);

  const cancelBuildList = useCallback(() => {
    subscriptionRef.current?.unsubscribe();
    subscriptionRef.current = null;
  }, []);

  const buildList = useCallback(() => {
    cancelBuildList();
    subscriptionRef.current = subscribeEditorCatalogList({
      getState: () => store.getState(),
      dispatch,
      onError: options?.onError,
    });
  }, [cancelBuildList, dispatch, options?.onError, store]);

  return {
    buildList,
    cancelBuildList,
    catalogPersistReady,
  };
};
